
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X, Check } from "lucide-react";
import { usStates } from "@/utils/states";

interface MarketSelectorProps {
  selectedMarkets: string[];
  setSelectedMarkets: (markets: string[]) => void;
}

const MarketSelector = ({ selectedMarkets, setSelectedMarkets }: MarketSelectorProps) => {
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState("");

  const toggleState = (stateId: string) => {
    if (selectedMarkets.includes(stateId)) {
      setSelectedMarkets(selectedMarkets.filter(id => id !== stateId));
    } else {
      setSelectedMarkets([...selectedMarkets, stateId]);
    }
  };

  const removeState = (stateId: string) => {
    setSelectedMarkets(selectedMarkets.filter(id => id !== stateId));
  };

  const filteredStates = usStates.filter(state => 
    state.name.toLowerCase().includes(stateSearchTerm.toLowerCase()) &&
    !selectedMarkets.includes(state.id)
  );

  return (
    <div className="space-y-2">
      <Label>Markets (Optional)</Label>
      
      {/* Selected States Display */}
      {selectedMarkets.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedMarkets.map(stateId => {
            const state = usStates.find(s => s.id === stateId);
            return (
              <Badge 
                key={stateId} 
                className="flex items-center gap-1 pl-2 pr-1 py-1.5 bg-card border-border text-foreground"
              >
                {state?.name}
                <button 
                  type="button" 
                  className="rounded-full p-0.5 hover:bg-muted"
                  onClick={() => removeState(stateId)}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
      
      {/* State Selection Interface */}
      <div className="relative">
        <Input
          placeholder="Select states you work in..."
          onClick={() => setShowStateDropdown(true)}
          onFocus={() => setShowStateDropdown(true)}
          value={stateSearchTerm}
          onChange={(e) => setStateSearchTerm(e.target.value)}
          className="bg-muted"
        />
        
        {showStateDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-md shadow-md">
            <div className="max-h-[200px] overflow-y-auto p-1">
              {filteredStates.length > 0 ? (
                filteredStates.map(state => (
                  <div
                    key={state.id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-muted cursor-pointer rounded-sm"
                    onClick={() => toggleState(state.id)}
                  >
                    <span>{state.name}</span>
                    <Check className="h-4 w-4 opacity-0" />
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-muted-foreground">
                  {stateSearchTerm ? "No states found" : "Type to search states"}
                </div>
              )}
            </div>
            <div className="border-t border-border p-2 flex justify-end">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setShowStateDropdown(false)}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        You can update your markets later in your profile
      </p>
    </div>
  );
};

export default MarketSelector;
